import clsx from 'clsx';
import { debounce } from 'lodash';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import React, { useCallback, useEffect, useState } from 'react';

import { pageTitle } from '../../components/shared/basic/customHead';
import { ShouldRender } from '../../components/shared/basic/ShouldRender';

const LiveCard = dynamic(() => import('../../components/Live/LiveCard'));
const HomepageSlider = dynamic(
  () => import('../../components/V2/HomePage/HomepageSlider'),
);

import { Typography } from 'antd';
import { useRouter } from 'next/router';
import { useRecoilValue } from 'recoil';

import LiveBannerCard from '@/components/Live/LiveBannerCard';
import FilterBar from '@/components/ResourcesPage/SchoolsPage/FilterBar';
import SkeletonCard from '@/components/shared/SkeletonCard';
import InfiniteGrid from '@/components/V2/HomePage/InfiniteGrid';

import { getCategoriesLive } from '@/services/blog';

import { metaTagProps } from '../schools';
import { user } from '../../atoms/user';
import LiveCardSkeleton from '../../components/Live/LiveCardSkeleton';
import EmptyState from '../../components/shared/EmptyState';
import { notify } from '../../components/shared/notify';
import HeroSection, {
} from '../../components/V2/HomePage/HeroSection';
import { EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import {
  getCurrentLive,
  getSingleOngoingLive,
  getUpcomingLive,
} from '../../services/Live';
import {
  GetCurrentLiveData,
  IStateHomePage,
} from '../../utils/interfaces/Live';
import { User } from '../../utils/interfaces/User';

const Live: React.FC<metaTagProps> = ({ metaData }) => {
  const [currentLiveData, setCurrentLiveData] = useState<GetCurrentLiveData[]>(
    [],
  );
  const [upcomingLive, setUpcomingLive] = useState<GetCurrentLiveData[]>([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [upcomingLiveLoading, setUpcomingLiveLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const [categories, setCategories] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [currentFilter, setCurrentFilter] = useState<{
    name: string;
    value: string;
  }>({ name: '', value: '' });
  const [loading, setLoading] = useState<boolean>(false);
  const [state, setState] = useState<IStateHomePage>({
    loading: false,
    token: '',
    liveSession: {
      id: 0,
      title: '',
      iframe: '',
      work_shop: {
        author_name: '',
      },
      desc: '',
      category: '',
      start_date: '',
      thumbnail: '',
      iframe_type: '',
      author: {
        name: '',
        zl_uid: '',
        id: '',
        my_follower_count: 0,
        is_verified: false,
        is_followed: false,
        profile_data: {
          avatar: '',
          bio: '',
          experience_years: '',
          emp_work_experience: {
            company: '',
            designation: '',
          },
          user_name: '',
        },
      },
    },
  });
  const userData: User | undefined = useRecoilValue(user);
  const router = useRouter();
  const { search: keyWord, filter } = router.query;

  const fetchCurrentLive = useCallback(async (keyword?: string) => {
    setLiveLoading(true);
    try {
      const { data } = await getCurrentLive(keyword);
      const transformedData: GetCurrentLiveData[] = data.map((item: any) => ({
        id: item.id,
        title: item.title,
        desc: item.description,
        start_time: item.start_time,
        end_time: item.end_time,
        start_date: item.date,
        live_workshop_id: item.id,
        iframe_type: item.iframe_type,
        iframe: item.iframe,
        work_shop_details: {
          slug: item.slug,
          title: item.title,
          id: item.id,
          author_name: item.author_name,
          category_name: item.category,
          author: {
            name: item.author_name,
            avatar: item.author?.profile_data?.avatar,
            user_name: item.author?.profile_data?.user_name,
            experience_years: item.author?.profile_data?.experience_years,
            emp_work_experience: {
              company: item.author?.profile_data?.emp_work_experience?.company,
              designation:
                item.author?.profile_data?.emp_work_experience?.designation,
            },
          },
        },

        live_workshop_session_thumbnails: {
          original_url: item.thumbnail,
        },
      }));

      setCurrentLiveData(transformedData);
    } catch (e) {
      console.warn(e);
    } finally {
      setLiveLoading(false);
    }
  }, []);

  useEffect(() => {
    if (filter && router.isReady && categories) {
      const foundCategory = categories.find(
        (category) => category.name === filter.toString(),
      );

      if (foundCategory) {
        const newFilter = {
          name: foundCategory.name,
          value: foundCategory.id,
        };
        setCurrentFilter(newFilter);
        setUpcomingLive([]);
      }
    }
  }, [router.isReady, filter, categories]);



  const fetchUpcomingLive = useCallback(
    async ({
      keyword,
      page = 1,
      category,
    }: {
      keyword?: string;
      page?: number;
      category?: string;
    }) => {
      setUpcomingLiveLoading(true);
      try {
        const response = await getUpcomingLive({
          keyword,
          page,
          category,
        });

        const transformedData: GetCurrentLiveData[] = response?.data.map(
          (item: any) => ({
            id: item.id,
            title: item.title,
            desc: item.description,
            start_time: item.start_time,
            end_time: item.end_time,
            start_date: item.date,
            live_workshop_id: item.id,
            registration_count: item.registration_count,
            is_registered: item.is_registered,
            work_shop_details: {
              slug: item.slug,
              title: item.title,
              id: item.id,
              author_name: item.author_name,
              category_name: item.category,
              author: {
                name: item.author_name,
                avatar: item.author?.profile_data?.avatar,
                experience_years: item.author?.profile_data?.experience_years,
                user_name: item.author?.profile_data?.user_name,
                emp_work_experience: {
                  company:
                    item.author?.profile_data?.emp_work_experience?.company,
                  designation:
                    item.author?.profile_data?.emp_work_experience?.designation,
                },
              },
            },
            live_workshop_session_thumbnails: {
              original_url: item.thumbnail,
            },
          }),
        );

        setUpcomingLive(
          page === 1
            ? transformedData
            : (prev) => [...prev, ...transformedData],
        );
        setHasMore(response?.meta.current_page !== response?.meta.last_page);
        setPage(response?.meta.current_page);
      } catch (e) {
        console.warn(e);
      } finally {
        setUpcomingLiveLoading(false);
      }
    },
    [],
  );
  const fetchSingleOngoingLive = async () => {
    setState((prev) => ({
      ...prev,
      loading: true,
    }));
    try {
      const { data } = await getSingleOngoingLive();
      const transformedData = {
        id: data.id,
        title: data.title,
        iframe: data.iframe,
        work_shop: {
          author_name: data.author_name || data.author?.name || '',
        },
        desc: data.description,
        start_date: data.date || '',
        category: data.category,
        start_time: data.start_time,
        end_time: data.end_time,
        iframe_type: data.iframe_type,
        thumbnail: data.thumbnail,
        author: {
          name: data.author?.name || '',
          zl_uid: data.author?.zl_uid || '',
          id: data.author?.id || '',
          my_follower_count: data.author?.my_follower_count || 0,
          is_verified: data.author?.is_verified || false,
          is_followed: data.author?.is_followed || false,
          profile_data: {
            avatar: data.author?.profile_data?.avatar || '',
            bio: data.author?.profile_data?.bio || '',
            experience_years: data.author?.profile_data?.experience_years || '',
            emp_work_experience: {
              company:
                data.author?.profile_data?.emp_work_experience?.company || '',
              designation:
                data.author?.profile_data?.emp_work_experience?.designation ||
                '',
            },
            user_name: data.author?.profile_data?.user_name || '',
          },
        },
      };

      setState((prev) => ({
        ...prev,
        liveSession: transformedData,
      }));
    } catch (e) {
      console.warn(e);
    } finally {
      setUpcomingLiveLoading(false);
      setState((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  useEffect(() => {
    if (currentFilter && currentFilter.name) {
      setUpcomingLive([]);
      setPage(1);
      setHasMore(true);

      fetchUpcomingLive({
        category: currentFilter.name === 'All' ? undefined : currentFilter.name,
        page: 1,
      });
    }
  }, [currentFilter]);

  const handleSearch = async (keyword: string) => {
    if (keyword !== undefined) {
      fetchCurrentLive(keyword);
      fetchUpcomingLive({ keyword, page: 1 });
    } else {
      fetchCurrentLive();
      fetchUpcomingLive({ page: 1 });
    }
  };

  const delayedSearch = debounce((keyword: string) => {
    handleSearch(keyword);
    if (keyWord) {
      const eventParams: EventDetails = {
        event_name: 'search_keyword',
        source: 'work_shop',
        source_type: 'search',
        source_id: null,
        sub_source: null,
        sub_source_id: null,
        unit: 'string',
        value: keyword,
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });
    }
  }, 500);

  useEffect(() => {
    delayedSearch(searchKeyword);
    return () => {
      delayedSearch.cancel();
    };
  }, [searchKeyword]);

  useEffect(() => {
    setSearchKeyword(keyWord as string);
  }, [keyWord]);

  useEffect(() => {
    fetchSingleOngoingLive();
    handleGetCategories();
  }, []);

  const handleGetCategories = async () => {
    try {
      setLoading(true);

      const { data } = await getCategoriesLive();

      const mappedCategories = [
        { id: '', name: 'All' },
        ...(data ?? []).map((name: string, index: number) => ({
          id: `${index + 1}`,
          name,
        })),
      ];

      setCategories(mappedCategories);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Head>
        <title>{pageTitle('Live')}</title>
        <meta
          name='description'
          content={metaData?.meta_description || ''}
          key='description'
        />
        <meta name='author' content={metaData?.author || ''} />
        <meta name='keywords' content={metaData?.keywords || ''} />

        <meta property='og:title' content={metaData?.name || ''} />
        <meta
          property='og:description'
          content={metaData?.meta_description || ''}
        />
      </Head>
      <div className='flex flex-col gap-y-4 md:pb-10'>
        <div>
          <ShouldRender check={!state?.loading}>
            {state.liveSession?.id ? (
              <div className='flex items-center justify-center '>
                <LiveBannerCard item={state} />
              </div>
            ) : (
              <HeroSection />
            )}
          </ShouldRender>
          <ShouldRender check={state?.loading}>
            <SkeletonCard variant='liveBannerCard' index={0} />
          </ShouldRender>
        </div>

        <ShouldRender check={liveLoading || currentLiveData?.length}>
          <div>
            <HomepageSlider
              heading='Ongoing Live'
              imageWidths={[320, 370, 382]}
              loading={!!currentLiveData?.length}
              isLive={true}
            >
              {!liveLoading
                ? currentLiveData.map((currentLive, index) => (
                  <span
                    key={`${currentLive.id}-${index}`}
                    className='p-1  transition-transform items-stretch transform hover:scale-[1.01] h-full duration-500'
                  >
                    <LiveCard item={currentLive} />
                  </span>
                ))
                : Array.from({ length: 10 }).map((_, index) => (
                  <span
                    key={index}
                    className='group cursor-pointer p-1 bg-transparent no-select'
                  >
                    <LiveCardSkeleton />
                  </span>
                ))}
            </HomepageSlider>
          </div>
        </ShouldRender>

        <ShouldRender check={upcomingLiveLoading || upcomingLive?.length}>
          <div
            className={clsx(
              upcomingLiveLoading && '',
              !currentLiveData?.length && ' lg:mt-10',
              'mb-10 px-4 md:px-5 lg:px-6 xl:px-12 ',
            )}
          >
            <div className='mb-7'>
              <Typography.Title
                level={2}
                className='text-base md:text-xl  lg:mt-1 mb-4 xl:text-xl text-white'
              >
                Upcoming Live
              </Typography.Title>
              <FilterBar
                categoryLoading={loading}
                data={categories}
                pageName='schools'
                selectedCategory={currentFilter?.value}
              />
            </div>
            <InfiniteGrid
              isLoading={upcomingLiveLoading}
              hasMore={hasMore}
              onPagination={() => fetchUpcomingLive({ page: page + 1 })}
            >
              {upcomingLive.map((upcomingLiveData, index) => (
                <span
                  key={`${upcomingLiveData.id}-${index}`}
                  className='p-1  transition-transform transform hover:scale-[1.01] h-full duration-500'
                >
                  <LiveCard item={upcomingLiveData} isUpcoming={true} />
                </span>
              ))}

              {upcomingLiveLoading &&
                Array.from({ length: 10 }).map((_, index) => (
                  <span
                    key={index}
                    className='group cursor-pointer p-1 md:p-2.5 bg-transparent no-select'
                  >
                    <LiveCardSkeleton />
                  </span>
                ))}
            </InfiniteGrid>
          </div>
        </ShouldRender>

        <ShouldRender check={!upcomingLive?.length && !currentLiveData?.length}>
          <EmptyState />
        </ShouldRender>
      </div>
    </>
  );
};

export const getStaticProps = async () => {
  try {
    const metaData = {
      title: 'Live',
      meta_description: 'Live Descriptions',
      author: 'Zebralearn',
      keywords: [''],
      name: 'Live',
    };

    return {
      props: {
        metaData,
      },
      revalidate: 60 * 60,
    };
  } catch (error) {
    notify('Error fetching metaData:', 'error');
    return {
      props: {
        metaData: null,
      },
      revalidate: 60 * 5,
    };
  }
};

export default Live;
